/**
 * Row view-model for the YaaS Client Console **Users** (accounts) table.
 *
 * Pure shaping over the list response from `useClientAccounts`. Balance /
 * earned / name are read straight off the list items once the endpoint
 * returns them. APY has no source on any payload, so it is null and the
 * table shows "—". Pending is a derived placeholder (see below).
 */

import type { ClientAccountsResponse, ParticipantAccountSettlementResult } from "@/types/clientAccounts";

const GROUP_ORDER: Array<keyof ClientAccountsResponse["groups"]> = [
    "needsAttention",
    "rewarded",
    "noSettlementHistory"
];

/** PLACEHOLDER — pending ≈ a fixed fraction of earned (no pending field
 *  exists). Matches the Positions table's `rewardsPendingOf` convention. */
const PLACEHOLDER_PENDING_FRACTION = 0.4;

export interface UserRow {
    uuid: string;
    /** Display name — PLACEHOLDER (no name field on the accounts API). */
    name: string;
    address: string;
    result: ParticipantAccountSettlementResult | null;
    /** Current USDv balance — real (from account detail). */
    balance: number;
    /** Lifetime USDv earned/distributed — real (from account detail). */
    earned: number;
    /** Trailing APY % — null until the accounts API carries a per-user rate. */
    apyPct: number | null;
    /** USDv pending — PLACEHOLDER (fraction of earned, no pending field). */
    pending: number;
}

function toNumber(value: string | null | undefined): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Flatten the grouped accounts response into a single Users list, joining in
 * per-user balance/earned from the detail responses and deriving the pending
 * placeholder.
 */
export function buildUserRows(groups: ClientAccountsResponse["groups"] | undefined): UserRow[] {
    if (!groups) return [];
    return GROUP_ORDER.flatMap((key) =>
        groups[key].accounts.map((account): UserRow => {
            const balance = toNumber(account.balance);
            const earned = toNumber(account.earned);
            return {
                uuid: account.uuid,
                name: account.name ?? "",
                address: account.address,
                result: account.result,
                balance,
                earned,
                apyPct: null,
                pending: earned * PLACEHOLDER_PENDING_FRACTION
            };
        })
    );
}
